import "./columnDrag.css";
import { persistFromDirective } from "../columnSettings/columnSettings";

var DEFAULT_LOCKED = ["actions", "row", "product"];
var DRAG_MIME = "application/x-column-drag";

function translateLabel(vnode, key, fallback) {
  try {
    if (vnode && vnode.context && typeof vnode.context.$t === "function") {
      var translated = vnode.context.$t(key);
      if (translated && translated !== key) return translated;
    }
  } catch (e) {
    // ignore i18n errors and use fallback
  }
  return fallback;
}

function parseOptions(binding) {
  var value = binding && binding.value;
  if (!value) {
    return {
      headers: null,
      visibleHeaders: null,
      locked: DEFAULT_LOCKED.slice(),
    };
  }
  if (Array.isArray(value)) {
    return {
      headers: value,
      visibleHeaders: value,
      locked: DEFAULT_LOCKED.slice(),
    };
  }
  return {
    headers: value.headers || null,
    visibleHeaders: value.visibleHeaders || value.headers || null,
    locked: Array.isArray(value.locked) ? value.locked.slice() : DEFAULT_LOCKED.slice(),
  };
}

function ensureMeta(headers) {
  if (!Array.isArray(headers)) return;
  headers.forEach(function (header, index) {
    if (!header) return;
    if (header.originalIndex == null) {
      header.originalIndex = index;
    }
    if (header.pinned == null) {
      header.pinned = false;
    }
  });
}

function isLocked(header, locked) {
  return !!(header && locked.indexOf(header.value) !== -1);
}

function isReorderable(header, locked) {
  return !!(header && !isLocked(header, locked) && !header.pinned);
}

function getTable(el) {
  if (!el) return null;
  if (el.tagName === "TABLE") return el;
  return el.querySelector("table");
}

function resolveVisibleHeaders(options) {
  if (Array.isArray(options.visibleHeaders)) return options.visibleHeaders;
  return options.headers || [];
}

function findHeader(headers, value) {
  if (!Array.isArray(headers)) return null;
  return (
    headers.find(function (header) {
      return header && header.value === value;
    }) || null
  );
}

function syncRestOriginalIndex(headers, locked) {
  var order = 1000;
  headers.forEach(function (header) {
    if (!isReorderable(header, locked)) return;
    header.originalIndex = order++;
  });
}

function moveReorderableColumn(headers, fromValue, toValue, locked) {
  if (!Array.isArray(headers) || !fromValue || !toValue || fromValue === toValue) {
    return false;
  }

  ensureMeta(headers);

  var lockedHeaders = [];
  var pinnedHeaders = [];
  var restHeaders = [];

  headers.forEach(function (header) {
    if (!header) return;
    if (isLocked(header, locked)) {
      lockedHeaders.push(header);
      return;
    }
    if (header.pinned) {
      pinnedHeaders.push(header);
      return;
    }
    restHeaders.push(header);
  });

  var fromIndex = restHeaders.findIndex(function (header) {
    return header.value === fromValue;
  });
  var toIndex = restHeaders.findIndex(function (header) {
    return header.value === toValue;
  });

  if (fromIndex < 0 || toIndex < 0) return false;

  var moved = restHeaders.splice(fromIndex, 1)[0];
  restHeaders.splice(toIndex, 0, moved);

  var next = lockedHeaders.concat(pinnedHeaders, restHeaders);
  headers.splice.apply(headers, [0, headers.length].concat(next));
  syncRestOriginalIndex(headers, locked);
  return true;
}

function clearDragButtons(table) {
  if (!table) return;
  table.querySelectorAll(".column-drag-btn").forEach(function (btn) {
    btn.remove();
  });
  table.querySelectorAll(".column-drag-th").forEach(function (th) {
    th.classList.remove("column-drag-th", "is-drag-over");
    th.removeAttribute("data-column-drag-value");
  });
}

function dragStateSignature(visible, locked) {
  if (!Array.isArray(visible)) return "";
  return visible
    .map(function (header) {
      if (!header) return "";
      return [
        header.value,
        header.pinned ? "1" : "0",
        isReorderable(header, locked) ? "1" : "0",
        header.originalIndex || 0,
      ].join(":");
    })
    .join("|");
}

function forceVueUpdate(el) {
  var vnode = el.__columnDragVnode;
  if (vnode && vnode.context && typeof vnode.context.$forceUpdate === "function") {
    vnode.context.$forceUpdate();
  }
}

function getThFromEvent(event) {
  var target = event.target;
  if (!target || !target.closest) return null;
  return target.closest("th.column-drag-th, th[data-column-drag-value]");
}

function ensureTableDelegation(el, table) {
  if (table.__columnDragDelegationBound) return;

  table.addEventListener("dragover", function (event) {
    var th = getThFromEvent(event);
    if (!th || !el.__columnDragSource) return;
    var options = parseOptions(el.__columnDragBinding);
    var targetValue = th.getAttribute("data-column-drag-value");
    var targetHeader = findHeader(options.headers, targetValue);
    if (!isReorderable(targetHeader, options.locked)) return;
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
    table.querySelectorAll(".column-drag-th.is-drag-over").forEach(function (node) {
      if (node !== th) node.classList.remove("is-drag-over");
    });
    th.classList.add("is-drag-over");
  });

  table.addEventListener("dragleave", function (event) {
    var th = getThFromEvent(event);
    if (!th) return;
    var related = event.relatedTarget;
    if (related && th.contains(related)) return;
    th.classList.remove("is-drag-over");
  });

  table.addEventListener("drop", function (event) {
    var th = getThFromEvent(event);
    if (!th) return;
    event.preventDefault();
    th.classList.remove("is-drag-over");

    var options = parseOptions(el.__columnDragBinding);
    var sourceValue =
      el.__columnDragSource ||
      (event.dataTransfer &&
        (event.dataTransfer.getData(DRAG_MIME) || event.dataTransfer.getData("text/plain")));
    var targetValue = th.getAttribute("data-column-drag-value");
    if (!sourceValue || !targetValue || sourceValue === targetValue) return;

    var sourceHeader = findHeader(options.headers, sourceValue);
    var targetHeader = findHeader(options.headers, targetValue);
    if (
      !isReorderable(sourceHeader, options.locked) ||
      !isReorderable(targetHeader, options.locked)
    ) {
      return;
    }

    var changed = moveReorderableColumn(
      options.headers,
      sourceValue,
      targetValue,
      options.locked
    );
    if (!changed) return;

    el.__columnDragSignature = null;
    // also invalidate pin/resize rebind helpers if present
    el.__columnPinSignature = null;
    el.__columnResizeCount = null;
    forceVueUpdate(el);
    persistFromDirective(
      el.__columnDragVnode,
      el.__columnDragBinding,
      options.headers
    );
    scheduleBind(el, el.__columnDragBinding, el.__columnDragVnode);
  });

  table.__columnDragDelegationBound = true;
}

function bindDragButtons(el, binding) {
  var options = parseOptions(el.__columnDragBinding || binding);
  var table = getTable(el);
  if (!table || !Array.isArray(options.headers)) return;

  ensureMeta(options.headers);
  ensureTableDelegation(el, table);

  var visible = resolveVisibleHeaders(options);
  var signature = dragStateSignature(visible, options.locked);

  if (
    el.__columnDragBound &&
    el.__columnDragSignature === signature &&
    table.querySelector(".column-drag-btn, .column-drag-th")
  ) {
    return;
  }

  clearDragButtons(table);

  var ths = table.querySelectorAll("thead tr > *");
  ths.forEach(function (th, index) {
    var header = visible[index];
    if (!header || !isReorderable(header, options.locked)) return;

    th.classList.add("column-drag-th");
    th.setAttribute("data-column-drag-value", header.value);

    var columnValue = header.value;
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "column-drag-btn";
    btn.draggable = true;
    btn.setAttribute(
      "data-tooltip",
      translateLabel(el.__columnDragVnode, "drag-column", "جابه‌جایی ستون")
    );

    var icon = document.createElement("i");
    icon.className = "mdi mdi-drag-vertical";
    icon.setAttribute("aria-hidden", "true");
    btn.appendChild(icon);

    btn.addEventListener("mousedown", function (event) {
      event.stopPropagation();
    });

    btn.addEventListener("dragstart", function (event) {
      event.stopPropagation();
      el.__columnDragSource = columnValue;
      document.body.classList.add("column-drag--dragging");
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData(DRAG_MIME, columnValue);
        event.dataTransfer.setData("text/plain", columnValue);
      }
      btn.classList.add("is-dragging");
    });

    btn.addEventListener("dragend", function () {
      el.__columnDragSource = null;
      document.body.classList.remove("column-drag--dragging");
      btn.classList.remove("is-dragging");
      table.querySelectorAll(".column-drag-th.is-drag-over").forEach(function (node) {
        node.classList.remove("is-drag-over");
      });
    });

    // keep drag as the first header action (right-most in RTL)
    if (th.firstChild) {
      th.insertBefore(btn, th.firstChild);
    } else {
      th.appendChild(btn);
    }
  });

  el.__columnDragBound = true;
  el.__columnDragSignature = signature;
}

function scheduleBind(el, binding, vnode) {
  el.__columnDragBinding = binding;
  el.__columnDragVnode = vnode;
  if (el.__columnDragTimer) {
    clearTimeout(el.__columnDragTimer);
  }
  el.__columnDragTimer = setTimeout(function () {
    bindDragButtons(el, binding);
  }, 0);
}

var ColumnDragDirective = {
  inserted: function (el, binding, vnode) {
    scheduleBind(el, binding, vnode);
  },
  componentUpdated: function (el, binding, vnode) {
    scheduleBind(el, binding, vnode);
  },
  unbind: function (el) {
    if (el.__columnDragTimer) {
      clearTimeout(el.__columnDragTimer);
      el.__columnDragTimer = null;
    }
    clearDragButtons(getTable(el));
    document.body.classList.remove("column-drag--dragging");
  },
};

export default {
  install: function (Vue) {
    Vue.directive("column-drag", ColumnDragDirective);
  },
};
